import useCellVisualizationUrlParams from 'components/cell-visualizations/useCellVisualizationUrlParams'
import { useEffect, useRef } from 'react'
import { CellVisualizationsState, PinnedMorphotype } from 'redux/slices'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { datasetItems } from 'redux/types'
import { getSession } from 'utils/api'
import { useAuth } from 'services/auth'
import { CELL_IMAGE_NORMAL_SIZE, CELL_IMAGE_NORMAL_SPACING } from '../shared'
import { getCellInfoToGroupsFromDataFrame } from '../utils'
import reconstructMorphotypeCoordinates from '../morphotypeDataOptimization'
import getSessionData from './getSessionData'
import { createDataFrame } from './arqueroHelpers'
import useMorphotypeMigration from '../useMorphotypeMigration'

/**
 * Builds a set of prefiltered cell IDs from morphotypesToRemove
 * These are cells that have been excluded via prefilter mode
 */
function buildPrefilteredCellIdSet(morphotypesToRemove: PinnedMorphotype[] | undefined): Set<string> {
  const prefilteredCellIdSet = new Set<string>()
  if (!morphotypesToRemove) return prefilteredCellIdSet

  morphotypesToRemove.forEach((morphotype) => {
    morphotype.cells?.points?.forEach((point) => {
      if (point.cellId) {
        prefilteredCellIdSet.add(point.cellId)
      }
    })
  })

  return prefilteredCellIdSet
}

export const useLoadCellInfoEffect = (
  onError: (error: unknown) => void,
  setMigrationDialogState?: (state: {
    open: boolean
    migrationStep: 'idle' | 'morphotypes' | 'differential-features' | 'complete'
    migratedMorphotypesCount: number
  }) => void
): void => {
  const { sessionId, versionId, updateVersion, isPreFilter } = useCellVisualizationUrlParams();
  const { setStore, cellVisualizations: { pinnedCells }, setSessionData } = useCellVisualizationsSlice();
  const { migrateIfNeeded } = useMorphotypeMigration()

  const loaded = useRef(false);
  const isGettingSession = useRef(false);
  const { isAuthenticated } = useAuth();
  const readyToGetData = sessionId && isAuthenticated && !loaded.current && !isGettingSession.current;


  useEffect(() => {
    if (!readyToGetData) return

    // TODO: Address this ticket: https://deepcellbio.atlassian.net/browse/SWT-381
    // This should go away when we address SWT-381
    const getArrowTable = async () => {
      const {
        status: sessionStatus,
        data: { data: sessionData },
      } = await getSession(sessionId, versionId)

      const { arrowBuffer }= await getSessionData({sessionId,sessionData,sessionStatus})
      isGettingSession.current = true;
      try {

        if (arrowBuffer && sessionData) {
          // Create DataFrame from Arrow buffer for coordinate reconstruction
          const dataFrame = createDataFrame(arrowBuffer)

          loaded.current = true;
          const { version_config: state, create_spec:createSpec } = sessionData;
          if (state) {

            const matchingDsi = datasetItems.find((dsi) => dsi.id === `${sessionId}`);
            const extraData: Partial<CellVisualizationsState> | undefined = isPreFilter
              ? {
                  selectedCellInfoGroupName: 'leiden',
                  cellImagesFilter: {
                    displayImages: true,
                    imageSize: CELL_IMAGE_NORMAL_SIZE / 1.5,
                    spacingAdjust: CELL_IMAGE_NORMAL_SPACING / 1.5,
                  },
                }
              : undefined

            if (!pinnedCells?.length) {
              // First reconstruct morphotypesToRemove to build prefilteredCellIdSet
              const reconstructedMorphotypesToRemove = state.morphotypesToRemove
                ? reconstructMorphotypeCoordinates(state.morphotypesToRemove, dataFrame)
                : undefined

              // Build set of prefiltered cell IDs from morphotypesToRemove
              const prefilteredCellIdSet = buildPrefilteredCellIdSet(reconstructedMorphotypesToRemove)

              // Check if migration is needed before showing dialog
              const isOldVersion =
                !state.morphotypeFormatVersion || state.morphotypeFormatVersion < 2
              const hasMorphotypes =
                (state.pinnedCells && state.pinnedCells.length > 0) ||
                (state.morphotypesToRemove && state.morphotypesToRemove.length > 0) ||
                (state.mergedPinnedCells && state.mergedPinnedCells.length > 0)
              const needsMigration = isOldVersion && hasMorphotypes

              // Show migration dialog at start if needed
              if (needsMigration && setMigrationDialogState) {
                setMigrationDialogState({
                  open: true,
                  migrationStep: 'idle',
                  migratedMorphotypesCount: 0,
                })
              }

              // Use DataFrame-based migration which handles:
              // - Coordinate reconstruction
              // - Migration from old format to new format (if needed)
              // - Differential features recomputation (if needed)
              const migrationResult = await migrateIfNeeded({
                pinnedCells: state.pinnedCells,
                morphotypesToRemove: state.morphotypesToRemove,
                mergedPinnedCells: state.mergedPinnedCells,
                morphotypeFormatVersion: state.morphotypeFormatVersion,
                differentialFeaturesComparisons: state.differentialFeaturesComparisons,
                dataFrame,
                prefilteredCellIdSet,
                sessionId,
                versionId: versionId ?? 0,
                onProgress: (step, count) => {
                  if (setMigrationDialogState) {
                    setMigrationDialogState({
                      open: true,
                      migrationStep: step,
                      migratedMorphotypesCount: count ?? 0,
                    })
                  }
                },
              })

              // Show completion dialog if migration occurred
              if (needsMigration && setMigrationDialogState) {
                setMigrationDialogState({
                  open: true,
                  migrationStep: 'complete',
                  migratedMorphotypesCount: migrationResult.recomputedDifferentialFeaturesComparisons?.length || 0,
                })
              }

              setStore({
                ...state,
                ...extraData,
                ...createSpec,
                // Store DataFrame singleton (not arrowBuffer) to prevent memory duplication
                // DataFrame already contains the parsed Arrow data (~200 MB)
                dataFrame,
                pinnedCells: migrationResult.migratedPinnedCells,
                morphotypesToRemove: migrationResult.reconstructedMorphotypesToRemove,
                mergedPinnedCells: migrationResult.reconstructedMergedPinnedCells,
                morphotypeFormatVersion: migrationResult.updatedMorphotypeFormatVersion,
                differentialFeaturesComparisons: migrationResult.recomputedDifferentialFeaturesComparisons,
                // Use DataFrame version to build cellInfoGroups
                cellInfoGroups: getCellInfoToGroupsFromDataFrame(dataFrame, state.cellInfoGroups),
                selectedDataset: matchingDsi ?? state.selectedDataset,
              })
            }
          }

          if (sessionData.version_id) {
            updateVersion(sessionData.version_id, isPreFilter)
          }
        }
        // Remove version_config from sessionData and save it to the slice
        if(sessionData !== undefined) {
          // Remove version_config from sessionData
          const filteredSessionData = {...sessionData}
          delete filteredSessionData.version_config
          setSessionData(filteredSessionData)
        }
      } catch (error) {
        onError(error)
      } finally {
        isGettingSession.current = false
      }
    }

    getArrowTable()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPreFilter,
    migrateIfNeeded,
    pinnedCells?.length,
    readyToGetData,
    sessionId,
    setMigrationDialogState,
    setStore,
    updateVersion,
    versionId,
    onError,
  ])

  useEffect(() => {
    isGettingSession.current = false
    loaded.current = false
  }, [sessionId])
}

export default useLoadCellInfoEffect
